import {
  Box,
  Button,
  ColumnFilterPopover,
  DRIVES_PER_PAGE,
  FilterDropDown,
  H2,
  Icon,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  ListDriveInfo,
  ModalContainer,
  MRT_PaginationState,
  MRT_Row,
  MRT_SortingState,
  MRT_TableOptions,
  RecordingProcessStatus,
  ResolveShareConfirm,
  RowActionButton,
  SharedRecording,
  Textarea,
  TextInput,
  UnshareWithOusterAlert,
  useDisclosure,
  BaseRecordingTableColumn,
  baseRecordingColumnMapping,
  RecordingStatus,
} from '@ouster/webviz-react-components';

import { IconButton } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { isEmpty } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { MdClear, MdRefresh, MdSearch, MdOutlineMessage } from 'react-icons/md';
import { RiMailAddLine, RiFolderSharedLine } from 'react-icons/ri';
import { useLocation, useNavigate } from 'react-router-dom';

import RecordingDownloadPopover from '../../components/recordingTable/RecordingDownloadPopover';
import { useColumnVisibility } from '../../components/recordingTable/useColumnVisibility';
import useFilters from '../../components/recordingTable/useFilters';
import { useSharedWithOusterListQuery } from '../../components/recordingTable/useRecordingListQuery';
import useChakraHook from '../../hooks/useChakraHook';
import { useRecordingSearch } from '../../hooks/useRecordingSearch';
import { useStoreActions, useStoreState } from '../../reducers';
import { convertFilterArrayToObject } from '../../utils/driveUtil';
import {
  createPaginationConfig,
  hasUserSelectionOnTable,
} from '../../utils/tableUtils';

const DEFAULT_COLUMN_VISIBILITY = {
  sdk_prod_sn: false,
  sdk_prod_hw_rev: false,
};
/* 
  The grouped filter content which will be shown as applied filters
*/
const FILTER_GROUPS = [
  {
    title: BaseRecordingTableColumn.ORGANIZATION,
    filterKeys: [
      baseRecordingColumnMapping[BaseRecordingTableColumn.ORGANIZATION],
    ],
  },
  {
    title: BaseRecordingTableColumn.SHARED_BY,
    filterKeys: [
      baseRecordingColumnMapping[BaseRecordingTableColumn.SHARED_BY],
    ],
  },
  {
    title: BaseRecordingTableColumn.SENSOR,
    filterKeys: [
      baseRecordingColumnMapping[BaseRecordingTableColumn.SENSOR],
      baseRecordingColumnMapping[BaseRecordingTableColumn.BEAM_SPACING],
      baseRecordingColumnMapping[BaseRecordingTableColumn.RESOLUTION],
    ],
  },
  {
    title: BaseRecordingTableColumn.HW,
    filterKeys: [
      baseRecordingColumnMapping[BaseRecordingTableColumn.HW],
      baseRecordingColumnMapping[BaseRecordingTableColumn.PRODUCT_GENERATION],
    ],
  },
  {
    title: BaseRecordingTableColumn.SN,
    filterKeys: [baseRecordingColumnMapping[BaseRecordingTableColumn.SN]],
  },
  {
    title: BaseRecordingTableColumn.FW,
    filterKeys: [baseRecordingColumnMapping[BaseRecordingTableColumn.FW]],
  },
  {
    title: BaseRecordingTableColumn.MODE,
    filterKeys: [baseRecordingColumnMapping[BaseRecordingTableColumn.MODE]],
  },
  {
    title: BaseRecordingTableColumn.TAGS,
    filterKeys: [baseRecordingColumnMapping[BaseRecordingTableColumn.TAGS]],
  },
];

const saveSearchToLocalStorage = (search: string, orgId: number) => {
  const searchKey = `share_search_${orgId}`;
  localStorage.setItem(searchKey, search);
};

type RowActionsProps = {
  row: MRT_Row<ListDriveInfo>;
  copyCloudVizLink: (value: ListDriveInfo) => void;
  showSharedNotes: (value: ListDriveInfo) => void;
  resolveIssue: (value: ListDriveInfo) => void;
  contactCustomer: (value: ListDriveInfo) => void;
  reprocessRecording: (value: ListDriveInfo) => void;
  openRecordingDownloadPopover: (
    row: ListDriveInfo,
    event?: React.MouseEvent<HTMLButtonElement>
  ) => void;
};

const RowActions = ({
  row,
  copyCloudVizLink,
  showSharedNotes,
  resolveIssue,
  contactCustomer,
  reprocessRecording,
  openRecordingDownloadPopover,
}: RowActionsProps) => {
  const { curUserOrg, isOusterUser } = useStoreState(
    (storeState) => storeState.user
  );
  return (
    <Box
      flexWrap="nowrap"
      display="flex"
      data-testid="shared-with-ouster-row-action-buttons"
    >
      {isOusterUser && curUserOrg?.role !== 'BASIC' && (
        <RowActionButton
          title="Reprocess the recording"
          icon={<Icon icon="refresh" />}
          row={row.original}
          onClickHandler={reprocessRecording}
        />
      )}
      <RowActionButton
        title="Copy the visualization link"
        icon={<Icon icon="share" />}
        row={row.original}
        onClickHandler={copyCloudVizLink}
      />
      <RowActionButton
        title="Download recording"
        icon={<Icon icon="download" />}
        row={row.original}
        onClickHandler={openRecordingDownloadPopover}
      />
      <RowActionButton
        title={row.original.notes ? 'View shared notes' : 'No notes shared'}
        icon={<MdOutlineMessage />}
        isDisabled={!row.original.notes}
        row={row.original}
        onClickHandler={showSharedNotes}
      />
      {!isOusterUser && (
        <RowActionButton
          title="Stop Sharing with Ouster"
          icon={<Icon icon="cancelSend" />}
          row={row.original}
          onClickHandler={resolveIssue}
        />
      )}
      {isOusterUser && (
        <RowActionButton
          title="Copy email"
          icon={<RiMailAddLine />}
          row={row.original}
          onClickHandler={contactCustomer}
        />
      )}
    </Box>
  );
};

export default function SharedWithOusterPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { showToast } = useChakraHook();
  const {
    isOpen: isResolvedAlertOpen,
    onOpen: onResolvedAlertOpen,
    onClose: onResolvedAlertClose,
  } = useDisclosure();

  const {
    isOpen: isNoteModalOpen,
    onOpen: onNoteModalOpen,
    onClose: onNoteModalClose,
  } = useDisclosure();

  const { isOusterUser, curUserOrg } = useStoreState(
    (storeState) => storeState.user
  );
  const {
    setSourceFileInfo,
    setOutputFileInfo,
    reprocessDrive,
    shareWithOuster,
  } = useStoreActions((actions) => actions.drive);

  // get filterable options
  const { loadSharedWithOusterFilterOptions } = useStoreActions(
    (actions) => actions.driveList
  );
  const { sharedWithOusterFilterOptions } = useStoreState((storeState) => ({
    ...storeState.driveList,
  }));
  const filterOptions = convertFilterArrayToObject(
    sharedWithOusterFilterOptions
  );

  const {
    filters,
    orderBy,
    sortOrder,
    pageIndex,
    isHydrated,
    setOrderBy,
    setPageIndex,
    setGroupFilters,
  } = useFilters('shared_time_stamp', 'desc');

  const [sorting, setSorting] = useState<MRT_SortingState>(() => [
    {
      id: orderBy || 'shared_time_stamp',
      desc: sortOrder ? sortOrder === 'desc' : true,
    },
  ]);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex,
    pageSize: DRIVES_PER_PAGE,
  });
  const tableContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const tableContainer = tableContainerRef.current?.querySelector(
      '.MuiTableContainer-root'
    ) as HTMLElement;
    if (tableContainer) {
      tableContainer.scrollTop = 0;
    }
  }, [pagination.pageIndex]);
  const [searchRestoredOrgId, setSearchRestoredOrgId] = useState<number | null>(
    null
  );
  const isSearchRestored =
    !!curUserOrg?.id && searchRestoredOrgId === curUserOrg.id;
  const isOrgChanging = Boolean(sessionStorage.getItem('orgChanged'));
  const isPaginationSynced = pagination.pageIndex === pageIndex;
  const isQueryEnabled =
    isHydrated && isSearchRestored && isPaginationSynced && !isOrgChanging;

  const { data, meta, isError, isRefetching, isLoading, isSuccess, refetch } =
    useSharedWithOusterListQuery(
      curUserOrg,
      orderBy,
      sortOrder,
      pagination.pageIndex,
      pagination.pageSize,
      location.search,
      isQueryEnabled
    );

  const [isReprocessing, setIsReprocessing] = useState(false);

  const { columnVisibility, setColumnVisibility } = useColumnVisibility({
    orgId: curUserOrg?.id,
    defaultVisibility: DEFAULT_COLUMN_VISIBILITY,
    configKey: 'share_with_ouster_column_config',
  });

  const copyToClipboard = async (text: string, type = 'Link') => {
    try {
      await navigator.clipboard.writeText(text);
      showToast({
        title: '',
        description: `${type} copied to clipboard`,
        status: 'success',
      });
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const copyCloudVizLink = (recording: ListDriveInfo) => {
    const url = `/organization/${recording.organization_id}/drive/${recording.drive_id}`;

    copyToClipboard(`${window.location.origin}${url}`);
  };

  const contactCustomer = (recording: ListDriveInfo) => {
    const testEmail = recording.sharer_email
      ? recording.sharer_email
      : 'No share email';
    /* 
      CALL api to get sharer email
    */
    copyToClipboard(testEmail, 'Email');
  };

  // the selected recording holds the ref of recording for viewing shared notes or unshare / resolve the issue
  const selectedRecordingRef = useRef<ListDriveInfo | null>(null);

  const showSharedNotes = (recording: ListDriveInfo) => {
    selectedRecordingRef.current = recording;
    onNoteModalOpen();
  };
  const onCloseNoteModalHandler = () => {
    selectedRecordingRef.current = null;
    onNoteModalClose();
  };

  const resolveIssue = (recording: ListDriveInfo) => {
    selectedRecordingRef.current = recording;
    onResolvedAlertOpen();
  };
  const onResolvedConfirm = () => {
    const unshare = async () => {
      const driveInfo = selectedRecordingRef.current;
      if (!curUserOrg?.id || !driveInfo?.drive_id) return;

      try {
        await shareWithOuster({
          organizationId: curUserOrg.id,
          driveId: driveInfo?.drive_id,
          shareWithOuster: false,
        });
        queryClient.invalidateQueries({ queryKey: ['shared-with-list'] });
        onResolvedAlertClose();
        showToast({
          title: 'Recording resolved',
          description: `Recording has been resolved and unshared with Ouster`,
          status: 'info',
        });
        selectedRecordingRef.current = null;
      } catch (error) {
        showToast({
          title: 'Failed to resolve the issue',
          description: `${error}`,
          status: 'error',
        });
      }
    };
    unshare();
  };

  const reprocessRecording = (recording: ListDriveInfo) => {
    setIsReprocessing(true);

    const reprocess = async () => {
      try {
        if (!recording || !recording.drive_id) return;
        setIsReprocessing(true);
        await reprocessDrive({
          orgId: recording.organization_id,
          driveId: recording.drive_id,
        });
        refetch();
        showToast({
          title: '',
          description: `Reprocessing recording ${recording.drive_id}`,
          status: 'success',
        });
      } catch (error) {
        showToast({
          title: 'Failed to reprocess recording',
          description: `${error}`,
          status: 'error',
        });
      } finally {
        setIsReprocessing(false);
      }
    };
    reprocess();
  };

  /* 
    Click on download button will triggered a popover with all downloadable links
  */
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [downloadRecordingId, setDownloadRecordingId] = useState('');
  const downloadRecordingOrgIdRef = useRef<number | undefined>(undefined);
  const downloadRecordingTitleRef = useRef<string | undefined>(undefined);
  const downloadRecordingIsSlamProcessingRef = useRef(false);

  const openRecordingDownloadPopover = (
    recording: ListDriveInfo,
    event?: React.MouseEvent<HTMLButtonElement>
  ) => {
    if (!event || !recording.drive_id) return;
    setAnchorEl(event.currentTarget);
    setDownloadRecordingId(recording.drive_id.toString());
    downloadRecordingOrgIdRef.current = recording.organization_id ?? undefined;
    downloadRecordingTitleRef.current = recording.recording_title ?? undefined;
    downloadRecordingIsSlamProcessingRef.current =
      recording.slam_generation_status === RecordingStatus.Running;
  };

  const closeDownloadMenu = () => {
    setOutputFileInfo([]);
    setSourceFileInfo([]);
    setAnchorEl(null);
  };

  const handlePaginationChange: MRT_TableOptions<ListDriveInfo>['onPaginationChange'] =
    (updater) => {
      setPagination((prev) => {
        const next =
          typeof updater === 'function'
            ? (updater as (p: MRT_PaginationState) => MRT_PaginationState)(prev)
            : updater;

        if (next.pageIndex !== prev.pageIndex) {
          setPageIndex(next.pageIndex);
        }

        return next;
      });
    };

  const handleSortingChange: MRT_TableOptions<ListDriveInfo>['onSortingChange'] =
    (updater) => {
      setSorting((prev) => {
        const next =
          typeof updater === 'function'
            ? (updater as (s: MRT_SortingState) => MRT_SortingState)(prev)
            : updater;

        if (!isHydrated || !next.length) {
          return next;
        }

        const { id: accessorKey, desc } = next[0];
        if (accessorKey && !accessorKey.startsWith('mrt-')) {
          setOrderBy(accessorKey, desc ? 'desc' : 'asc');
        }

        return next;
      });
    };

  const customTableOptions: Partial<MRT_TableOptions<ListDriveInfo>> = {
    onPaginationChange: handlePaginationChange,
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    renderRowActions: ({ row }) => (
      <RowActions
        row={row}
        copyCloudVizLink={copyCloudVizLink}
        showSharedNotes={showSharedNotes}
        openRecordingDownloadPopover={openRecordingDownloadPopover}
        contactCustomer={contactCustomer}
        resolveIssue={resolveIssue}
        reprocessRecording={reprocessRecording}
      />
    ),
    enableColumnPinning: false,
    enableHiding: true,
    enableTopToolbar: true,
    enableSortingRemoval: false,
    rowCount: meta?.totalCount ?? 0,
    state: {
      isLoading: isLoading || isReprocessing,
      pagination,
      showLoadingOverlay: false,
      showAlertBanner: isError,
      showProgressBars: isLoading || isRefetching,
      showSkeletons: isLoading || isRefetching || isReprocessing,
      sorting,
      columnVisibility,
    },
    ...createPaginationConfig(meta, pagination),
  };

  useEffect(() => {
    if (!isHydrated) return;

    setPagination((prev) => {
      if (prev.pageIndex === pageIndex) {
        return prev;
      }

      return { ...prev, pageIndex };
    });

    setSorting((prev) => {
      const current = prev[0];
      const nextDesc = sortOrder === 'desc';
      if (current?.id === orderBy && current?.desc === nextDesc) {
        return prev;
      }

      return [{ id: orderBy, desc: nextDesc }];
    });
  }, [isHydrated, orderBy, pageIndex, sortOrder]);

  useEffect(() => {
    if (!curUserOrg?.id || isSearchRestored) return;

    if (location.search === '') {
      const storedSearch = localStorage.getItem(
        `share_search_${curUserOrg.id}`
      );
      if (storedSearch) {
        navigate({ search: storedSearch }, { replace: true });
      }
    }

    setSearchRestoredOrgId(curUserOrg.id);
  }, [curUserOrg?.id, isSearchRestored, location.search, navigate]);

  useEffect(() => {
    if (
      curUserOrg?.id &&
      isHydrated &&
      isSuccess &&
      !sessionStorage.getItem('orgChanged')
    ) {
      saveSearchToLocalStorage(location.search, curUserOrg.id);
    }
  }, [isHydrated, isSuccess, location.search, curUserOrg?.id]);

  /* 
    Whenever user switch organization. We need to clear the previous filters and invalidate query
  */
  useEffect(() => {
    if (curUserOrg?.id && sessionStorage.getItem('orgChanged')) {
      queryClient.invalidateQueries({ queryKey: ['shared-with-list'] });
      // get search query based on orgId
      const storedSearch = localStorage.getItem(
        `share_search_${curUserOrg.id}`
      );

      navigate({
        pathname: location.pathname,
        search: storedSearch || '',
      });
      sessionStorage.removeItem('orgChanged');
    }
  }, [curUserOrg?.id, location.pathname, navigate, queryClient]);

  useEffect(() => {
    if (curUserOrg?.id) {
      loadSharedWithOusterFilterOptions(curUserOrg.id);
    }
  }, [curUserOrg?.id, loadSharedWithOusterFilterOptions]);

  const filterFields = useMemo(
    () => [
      baseRecordingColumnMapping[BaseRecordingTableColumn.STATUS],
      baseRecordingColumnMapping[BaseRecordingTableColumn.ORGANIZATION],
      baseRecordingColumnMapping[BaseRecordingTableColumn.SHARED_BY],
      baseRecordingColumnMapping[BaseRecordingTableColumn.DUR],
      baseRecordingColumnMapping[BaseRecordingTableColumn.PRODUCT_TYPE],
      baseRecordingColumnMapping[BaseRecordingTableColumn.RESOLUTION],
      baseRecordingColumnMapping[BaseRecordingTableColumn.BEAM_SPACING],
      baseRecordingColumnMapping[BaseRecordingTableColumn.MODE],
      baseRecordingColumnMapping[BaseRecordingTableColumn.FW],
      baseRecordingColumnMapping[BaseRecordingTableColumn.HW],
      baseRecordingColumnMapping[BaseRecordingTableColumn.SN],
      baseRecordingColumnMapping[BaseRecordingTableColumn.TAGS],
      baseRecordingColumnMapping[BaseRecordingTableColumn.RECORDING_TITLE],
    ],
    []
  );

  const { searchTerm, setSearchTerm, filteredData } = useRecordingSearch(
    data,
    filterFields,
    new URLSearchParams(location.search).get('q') || ''
  );

  // Restore search term from URL after navigation (when q is in the restored URL)
  useEffect(() => {
    const urlQ = new URLSearchParams(location.search).get('q') || '';
    if (urlQ && searchTerm !== urlQ) {
      setSearchTerm(urlQ);
    }
    // Only run when location.search changes during restoration
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Sync search term to URL `q` param
  const searchTermRef = useRef(searchTerm);
  searchTermRef.current = searchTerm;
  useEffect(() => {
    if (!isHydrated || !isSearchRestored) return;

    const params = new URLSearchParams(location.search);
    const currentQ = params.get('q') || '';
    if (searchTerm !== currentQ) {
      if (searchTerm) {
        params.set('q', searchTerm);
      } else {
        params.delete('q');
      }
      navigate({ search: params.toString() }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, isHydrated, isSearchRestored, navigate]);

  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [selectedColKeys, setSelectedColKeys] = useState<string[]>([]);
  const handleCloseFilterPopover = () => {
    setFilterAnchorEl(null);
  };

  const openVizPage = (recording: ListDriveInfo) => {
    if (isLoading) return;
    if (hasUserSelectionOnTable()) return;
    if (recording.status !== RecordingProcessStatus.Processing) {
      const url = `/organization/${recording.organization_id}/drive/${recording.drive_id}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const clearAllFilters = () => {
    if (curUserOrg?.id) {
      localStorage.removeItem(`share_search_${curUserOrg.id}`);
    }

    navigate({
      pathname: location.pathname,
      search: '', // Clear search parameters
    });
  };

  const totalSelectedFilters = Object.values(filters).reduce(
    (total, filterValues) => total + filterValues.length,
    0
  );

  if (!curUserOrg) return null;

  /* 
    For Ouster Basic user, they should not see the shared with ouster page. For any reason user lands on it, we should redirect them to private drive page
  */
  if (isOusterUser && curUserOrg.role === 'BASIC') {
    navigate('/drive/private', { replace: true });
  }

  if (isSuccess && data?.length === 0 && isEmpty(filters))
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
        flexDirection="column"
        gap={4}
      >
        <Box as="p" fontSize={24} maxWidth="45ch" textAlign="center">
          No data shared with Ouster yet.
        </Box>
        {!isOusterUser && (
          <Box
            fontSize={18}
            maxWidth="65ch"
            textAlign="center"
            mb={8}
            lineHeight={1.7}
            gap={4}
            display="grid"
            style={{ textWrap: 'balance' }}
          >
            <Box>
              To share a recording, simply open any recording&apos;s
              visualization from <strong>Cloud Recordings</strong> page. Then,
              click the <strong>cog</strong> icon on the top right, and select{' '}
              <strong>Share recording with Ouster</strong> menu option from the
              dropdown.
            </Box>
            <Box>
              You can explain the reason for sharing in the following dialog,
              and the Ouster support team will reach out to you shortly.
            </Box>
          </Box>
        )}
        <RiFolderSharedLine size={72} />
      </Box>
    );

  return (
    <>
      <Box
        p="25px 36px 0px"
        h="100%"
        overflow="hidden"
        position="relative"
        display="grid"
        gridTemplateRows="auto 1fr"
        gap="1rem"
        data-testid="shared-with-ouster-root"
      >
        <Box display="grid" rowGap="1rem">
          <Box
            display="flex"
            placeContent="space-between"
            alignItems="center"
            mb={6}
          >
            <H2 fontWeight="medium" fontSize={30}>
              Recordings Shared with Ouster
            </H2>
            <Button
              variant="ghost"
              colorScheme="gray"
              gap={1}
              onClick={() => refetch()}
              data-testid="shared-with-ouster-refresh"
            >
              <MdRefresh size={18} /> Refresh List
            </Button>
          </Box>
          {/* filters and search controls */}
          <Box
            display="flex"
            gap={2}
            alignItems="center"
            flexWrap="wrap"
            mr="50px"
            zIndex={2}
            data-testid="shared-with-ouster-filters"
          >
            <InputGroup
              maxW="25ch"
              data-testid="shared-with-ouster-search-group"
            >
              <InputLeftElement pointerEvents="none">
                <MdSearch size={18} />
              </InputLeftElement>
              <TextInput
                pl="2.5rem"
                pr="3.5rem"
                placeholder="Search ..."
                type="search"
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchTerm(e.target.value)
                }
                data-testid="shared-with-ouster-search-input"
              />
              <InputRightElement>
                <IconButton
                  onClick={() => setSearchTerm('')}
                  data-testid="shared-with-ouster-search-clear"
                >
                  <MdClear size={16} />
                </IconButton>
              </InputRightElement>
            </InputGroup>

            {FILTER_GROUPS.map((groupedFilter) => (
              <FilterDropDown
                title={groupedFilter.title}
                filterKey={groupedFilter.filterKeys}
                filterOptions={filterOptions}
                setGroupFilters={setGroupFilters}
                selectedFilters={filters}
                key={groupedFilter.title}
                data-testid={`shared-with-ouster-filter-${groupedFilter.title
                  .toLowerCase()
                  .replace(/\s/g, '-')}`}
              />
            ))}

            {totalSelectedFilters > 0 && (
              <Button
                variant="ghost"
                colorScheme="gray"
                gap={1}
                onClick={() => clearAllFilters()}
                fontSize="12px"
                data-testid="shared-with-ouster-clear-filters"
              >
                <MdClear size={18} /> Clear Filters
              </Button>
            )}
          </Box>
        </Box>

        <Box
          overflow="hidden"
          position="relative"
          bg="rgb(29, 29, 29)"
          mt="-56px"
          zIndex={1}
          ref={tableContainerRef}
          data-testid="shared-with-ouster-table-container"
        >
          <SharedRecording
            key={curUserOrg?.id}
            filters={{}}
            maxHeight="calc(100vh - 320px)"
            filteredData={filteredData}
            setFilterAnchorEl={setFilterAnchorEl}
            setSelectedColKeys={setSelectedColKeys}
            openVizPage={openVizPage}
            customTableOptions={customTableOptions}
            isOuster={isOusterUser}
            data-testid="shared-with-ouster-table"
          />
        </Box>
      </Box>
      {anchorEl && (
        <RecordingDownloadPopover
          anchorEl={anchorEl}
          closeDownloadMenu={closeDownloadMenu}
          downloadRecordingId={downloadRecordingId}
          orgId={downloadRecordingOrgIdRef.current}
          recordingTitle={downloadRecordingTitleRef.current}
          isSlamProcessing={downloadRecordingIsSlamProcessingRef.current}
          data-testid="shared-with-ouster-download-popover"
        />
      )}

      {filterAnchorEl && (
        <ColumnFilterPopover
          anchorEl={filterAnchorEl}
          onClose={handleCloseFilterPopover}
          filterKey={selectedColKeys}
          filterOptions={filterOptions}
          selectedFilters={filters}
          setGroupFilters={setGroupFilters}
          data-testid="shared-with-ouster-column-filter-popover"
        />
      )}

      {isOusterUser ? (
        <ResolveShareConfirm
          isOpen={isResolvedAlertOpen}
          onClose={onResolvedAlertClose}
          onConfirm={onResolvedConfirm}
        />
      ) : (
        <UnshareWithOusterAlert
          isUnshareAlertOpen={isResolvedAlertOpen}
          onUnshareAlertClose={onResolvedAlertClose}
          onUnshareConfirm={onResolvedConfirm}
        />
      )}

      <ModalContainer
        isOpen={isNoteModalOpen}
        onClose={onCloseNoteModalHandler}
        header="Notes shared with recording"
        footer={null}
        content={
          <Textarea
            lineHeight="1.6rem"
            minW={480}
            minH={250}
            value={selectedRecordingRef.current?.notes}
            readOnly
            data-testid="shared-with-ouster-notes-textarea"
          />
        }
        data-testid="shared-with-ouster-notes-modal"
      />
    </>
  );
}
